import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Stack,
    Button
} from '@mui/material';
import { Print as PrintIcon, FileDownload as DownloadIcon } from '@mui/icons-material';
import { formatCurrency } from '../../../utils/compteClientEngine';
import { jsonToXlsx } from '../../../settings/themeSettings';

const ListeSelectionViewer = ({
    selectionNumber = 'SEL-2026-09-001',
    periode = { start: '01/09/2026', end: '30/09/2026' },
    factures = [],
    comptesRecap = [],
    user = 'Comptable AFT'
}) => {
    const dateTirage = new Date().toLocaleDateString('fr-FR') + ' à ' + new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    const totalHT = factures.reduce((s, f) => s + Number(f.montantHT || 0), 0);
    const totalTVA = factures.reduce((s, f) => s + Number(f.tva || 0), 0);
    const totalTTC = factures.reduce((s, f) => s + Number(f.montantTTC || 0), 0);

    const totalDebitRecap = comptesRecap.reduce((s, c) => s + Number(c.totalDebit || 0), 0);
    const totalCreditRecap = comptesRecap.reduce((s, c) => s + Number(c.totalCredit || 0), 0);

    const handleExportExcel = () => {
        const exportData = factures.map(f => ({
            'N° Facture': f.numeroFacture,
            'Date': f.dateFacture,
            'Client': f.clientNom,
            'Compte Tiers': f.clientCodeTiers,
            'N° Dossier / PNR': f.refDossier,
            'Nature': f.nature,
            'Journal': f.journalCode || 'FAC',
            'Montant HT (Marge CFA)': f.montantHT,
            'TVA (CFA)': f.tva,
            'Montant TTC (CFA)': f.montantTTC
        }));
        jsonToXlsx(exportData, `Liste_Selection_${selectionNumber}`);
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <Box sx={{ outline: 'none' }}>
            {/* TOOLBAR */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} className="no-print">
                <Typography variant="body2" color="text.secondary">
                    Document de contrôle — {factures.length} facture{factures.length > 1 ? 's' : ''} sélectionnée{factures.length > 1 ? 's' : ''}
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Imprimer le document
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Export Excel
                    </Button>
                </Stack>
            </Stack>

            {/* DOCUMENT OFFICIEL IMPRIMABLE (PAGE 11 NOTE SAGE) */}
            <Paper
                elevation={0}
                className="printable-document"
                sx={{
                    p: 3,
                    border: '2px solid #0f172a',
                    borderRadius: '8px',
                    bgcolor: 'white',
                    maxWidth: '1100px',
                    mx: 'auto'
                }}
            >
                {/* EN-TÊTE TRANSVERSAL */}
                <Box sx={{ borderBottom: '2px solid #0f172a', pb: 1.5, mb: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1.2fr', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="body1" fontWeight="bold" sx={{ color: '#0f172a' }}>
                                AFRICA FOR TOURISM SARL
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Agence de Voyages & Tourisme
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                TravelManager v2.0
                            </Typography>
                        </Box>

                        <Box textAlign="center">
                            <Typography variant="h6" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: '#0f172a' }}>
                                Liste des écritures sélectionnées pour transfert comptable
                            </Typography>
                        </Box>

                        <Box textAlign="right">
                            <Typography variant="caption" display="block" color="text.secondary">
                                Date de tirage : {dateTirage}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                                Page : 1 / 1
                            </Typography>
                        </Box>
                    </Box>

                    {/* GRILLE DES MÉTADONNÉES */}
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '4px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <Box>
                            <Typography variant="caption" display="block">
                                <strong>Période de facturation :</strong> du {periode.start} au {periode.end}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Édité le :</strong> {dateTirage}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Nombre de factures :</strong> {factures.length}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" display="block">
                                <strong>N° de sélection :</strong> {selectionNumber}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Édité par :</strong> {user}
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ color: '#2563eb', fontWeight: 'bold' }}>
                                <strong>Statut :</strong> Sélection en attente de contrôle
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* 1. TABLEAU DES FACTURES SÉLECTIONNÉES */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#0f172a' }}>
                    Factures sélectionnées
                </Typography>
                <Box sx={{ border: '1px solid #0f172a', borderRadius: '4px', overflow: 'hidden', mb: 3 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '120px 90px 1.5fr 110px 120px 100px 70px 100px 90px 110px', bgcolor: '#0f172a', color: 'white', p: 1, fontWeight: 'bold', fontSize: '0.78rem' }}>
                        <Box>N° facture</Box>
                        <Box>Date</Box>
                        <Box>Client</Box>
                        <Box>Compte tiers</Box>
                        <Box>N° dossier / PNR</Box>
                        <Box>Nature</Box>
                        <Box>Journal</Box>
                        <Box sx={{ textAlign: 'right' }}>Montant HT</Box>
                        <Box sx={{ textAlign: 'right' }}>TVA</Box>
                        <Box sx={{ textAlign: 'right' }}>Montant TTC</Box>
                    </Box>

                    {factures.map((f, idx) => (
                        <Box
                            key={f.id || idx}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '120px 90px 1.5fr 110px 120px 100px 70px 100px 90px 110px',
                                p: 0.9,
                                alignItems: 'center',
                                fontSize: '0.8rem',
                                borderBottom: '1px solid #e2e8f0',
                                bgcolor: idx % 2 === 0 ? 'white' : '#f8fafc'
                            }}
                        >
                            <Box sx={{ fontWeight: 600 }}>{f.numeroFacture}</Box>
                            <Box sx={{ color: 'text.secondary' }}>{f.dateFacture}</Box>
                            <Box sx={{ fontWeight: 600 }}>{f.clientNom}</Box>
                            <Box sx={{ color: f.clientCodeTiers === '4111Àcréer' ? '#dc2626' : '#2563eb', fontWeight: 600 }}>
                                {f.clientCodeTiers}
                            </Box>
                            <Box sx={{ color: 'text.secondary' }}>{f.refDossier}</Box>
                            <Box>{f.nature}</Box>
                            <Box>{f.journalCode || 'FAC'}</Box>
                            <Box sx={{ textAlign: 'right' }}>{formatCurrency(f.montantHT)}</Box>
                            <Box sx={{ textAlign: 'right' }}>{f.tva > 0 ? formatCurrency(f.tva) : '0'}</Box>
                            <Box sx={{ textAlign: 'right', fontWeight: 700 }}>{formatCurrency(f.montantTTC)}</Box>
                        </Box>
                    ))}

                    {/* TOTAUX FACTURES */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '120px 90px 1.5fr 110px 120px 100px 70px 100px 90px 110px',
                            bgcolor: '#f1f5f9',
                            p: 1.1,
                            fontWeight: 'bold',
                            fontSize: '0.82rem',
                            borderTop: '2px solid #0f172a'
                        }}
                    >
                        <Box sx={{ gridColumn: 'span 7', textAlign: 'right', pr: 2 }}>
                            TOTAUX (FCFA) :
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>{formatCurrency(totalHT)}</Box>
                        <Box sx={{ textAlign: 'right' }}>{formatCurrency(totalTVA)}</Box>
                        <Box sx={{ textAlign: 'right', fontWeight: 900 }}>{formatCurrency(totalTTC)}</Box>
                    </Box>
                </Box>

                {/* 2. RÉCAPITULATIF DES COMPTES QUI SERONT MOUVEMENTÉS */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#0f172a' }}>
                    Récapitulatif des comptes qui seront mouvementés
                </Typography>
                <Box sx={{ border: '1px solid #0f172a', borderRadius: '4px', overflow: 'hidden', mb: 3 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr 100px 140px 140px', bgcolor: '#0f172a', color: 'white', p: 1, fontWeight: 'bold', fontSize: '0.78rem' }}>
                        <Box>Compte</Box>
                        <Box>Intitulé</Box>
                        <Box>Journal</Box>
                        <Box sx={{ textAlign: 'right' }}>Total Débit</Box>
                        <Box sx={{ textAlign: 'right' }}>Total Crédit</Box>
                    </Box>

                    {comptesRecap.map((c, idx) => (
                        <Box
                            key={c.compte || idx}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '120px 1fr 100px 140px 140px',
                                p: 0.9,
                                alignItems: 'center',
                                fontSize: '0.8rem',
                                borderBottom: '1px solid #e2e8f0',
                                bgcolor: idx % 2 === 0 ? 'white' : '#f8fafc'
                            }}
                        >
                            <Box sx={{ fontWeight: 700 }}>{c.compte}</Box>
                            <Box>{c.intitule}</Box>
                            <Box>{c.journal}</Box>
                            <Box sx={{ textAlign: 'right', fontWeight: c.totalDebit > 0 ? 700 : 400 }}>
                                {c.totalDebit > 0 ? formatCurrency(c.totalDebit) : '—'}
                            </Box>
                            <Box sx={{ textAlign: 'right', fontWeight: c.totalCredit > 0 ? 700 : 400 }}>
                                {c.totalCredit > 0 ? formatCurrency(c.totalCredit) : '—'}
                            </Box>
                        </Box>
                    ))}

                    {/* ÉQUILIBRE DÉBIT / CRÉDIT */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '120px 1fr 100px 140px 140px',
                            bgcolor: '#f1f5f9',
                            p: 1.1,
                            fontWeight: 'bold',
                            fontSize: '0.85rem',
                            borderTop: '2px solid #0f172a'
                        }}
                    >
                        <Box sx={{ gridColumn: 'span 3', textAlign: 'right', pr: 2 }}>
                            ÉQUILIBRE (Débit = Crédit) :
                        </Box>
                        <Box sx={{ textAlign: 'right', fontWeight: 900 }}>{formatCurrency(totalDebitRecap)}</Box>
                        <Box sx={{ textAlign: 'right', fontWeight: 900 }}>{formatCurrency(totalCreditRecap)}</Box>
                    </Box>
                </Box>

                {/* ZONE DE VISA COMPTABLE */}
                <Box sx={{ border: '1px solid #cbd5e1', borderRadius: '4px', p: 1.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                        <Typography variant="caption" display="block">
                            <strong>Contrôlé par (le comptable) :</strong> {user}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" display="block">
                            <strong>Visa / Date :</strong> _____________________________
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default ListeSelectionViewer;
